import { useEffect, useState } from "react";

import {
  ArrowButton,
  Select,
  TextField,
  TableMetadata,
  TableStateWithFilters,
  useTable,
  Label,
} from "@king-ict/eupisi-ui/components";
import { PagedResult, CodeBook } from "@king-ict/eupisi-ui/models";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchRegionalCommittee } from "../../api/Committee/RegionalCommitteeApi";
import {
  ContentArea,
  ToolbarTop,
  SearchCard,
  ListTable,
} from "../../components";
import { useAuthCounties } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  RegionalCommittee,
  RegionalCommitteeSearch,
  RegionalCommitteeSort,
  CommitteeStatus,
} from "../../models/Committee";
import { ImportantDateEventEnum } from "../../models/ImportantDate/ImportantDateEventEnum";
import { ClaimType } from "../../models/User";
import { useGetSchoolYearCurrentQuery } from "../../queries/codeBookQuery";
import { regionalCommitteeEditUrl } from "../../routes/routes";
import { FormatUtils, MsgUtils, TableUtils } from "../../utils";

export function RegionalCommitteeList() {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();
  const authCounties = useAuthCounties();

  //STORAGE KEY
  const storageKey = "RegionalCommitteeSearchTableState";
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const createInitialTableState = () => {
    return TableUtils.CreateInitialTableState<RegionalCommitteeSearch>(
      storageKey,
      TableUtils.tableDefaultState(RegionalCommitteeSort.County)
    );
  };
  const [initialTableState] = useState<
    TableStateWithFilters<RegionalCommitteeSearch>
  >(() => createInitialTableState());
  //INIT SEARCH FILTER
  const createInitialCommitteeSearch = (): RegionalCommitteeSearch => {
    return {
      countyId:
        initialTableState.countyId ??
        (auth.currentRole?.claimType === ClaimType.County &&
        (auth.currentRole?.locations?.length ?? 0) > 0
          ? auth!.currentRole!.locations![0]
          : 1),
    };
  };
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<RegionalCommittee>();
  const [tableData, setTableData] =
    useState<PagedResult<RegionalCommittee>>(defaultTableData);
  //SET SEARCH FILTERS
  const [expertCommissionSearch, setExpertCommissionSearch] =
    useState<RegionalCommitteeSearch>(() => createInitialCommitteeSearch());
  const [tableSearch, setTableSearch] = useState<RegionalCommitteeSearch>(() =>
    createInitialCommitteeSearch()
  );
  const [filterChanged, setFilterChanged] = useState<boolean>(false);
  const [clearFilter, setClearFilter] = useState<boolean>(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<RegionalCommittee> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "county",
        header: t("common.expertCommission.county"),
        accessor: (item: RegionalCommittee) => {
          return (
            <div>{authCounties.find((v) => v.id === item.countyId)?.name}</div>
          );
        },
        sortBy: RegionalCommitteeSort.County,
      },
      {
        columnKey: "name",
        header: t("common.expertCommission.name"),
        accessor: (item: RegionalCommittee) => {
          return <div>{item.name}</div>;
        },
        sortBy: RegionalCommitteeSort.Name,
      },
      {
        columnKey: "dateFromDateTo",
        header: t("common.expertCommission.dateFromDateTo"),
        accessor: (item: RegionalCommittee) => {
          return (
            <div>
              {item.dateFrom ? hrDate(item.dateFrom) : ""} -{" "}
              {item.dateTo ? hrDate(item.dateTo) : ""}
            </div>
          );
        },
        sortBy: RegionalCommitteeSort.DateFrom,
      },
      {
        columnKey: "status",
        header: t("common.status"),
        accessor: (item: RegionalCommittee) => {
          return (
            <>
              <Label
                color={FormatUtils.getCommitteeStatusColor(item.statusId)}
                className="text-center"
                outline={false}
                fullWidth={false}
              >
                {statuses.find((v) => v.id === item.statusId)?.name}
              </Label>
            </>
          );
        },
        sortBy: RegionalCommitteeSort.Status,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: RegionalCommittee) => {
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //FILTER DATA
  const [tableProps, tableState, setFilters] =
    useTable<RegionalCommitteeSearch>({
      metadata: tableMetadata,
      data: tableData!.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: tableData!.rowCount,
      defaultSort: {
        order: initialTableState.order,
        sortBy: initialTableState.sortBy,
      },
    });
  //FILTER DATA
  const [statuses] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(CommitteeStatus, "CommitteeStatus")
  );
  const { data: schoolYear } = useGetSchoolYearCurrentQuery();
  const [isEditable, setIsEditable] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      if (schoolYear) {
        const importantDate = schoolYear.importantDates.find(
          (val) =>
            val.eventId === ImportantDateEventEnum.InstitutionAdministration
        );
        if (importantDate) {
          const exactlyNow = new Date(Date.now());
          const dateFrom = new Date(importantDate.dateTimeFrom);
          const dateTo = new Date(importantDate.dateTimeTo);
          if (dateFrom <= exactlyNow && dateTo >= exactlyNow) {
            setIsEditable(true);
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolYear]);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertCommissionSearch]);

  useEffect(() => {
    (async () => {
      var request = TableUtils.GetSearchRequest<RegionalCommitteeSearch>(
        tableSearch,
        tableState
      );
      var itemList = await searchRegionalCommittee(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleNewExpertCommissionClicked = () => {
    if (expertCommissionSearch.countyId !== undefined) {
      TableUtils.SetSessionStorageItem<RegionalCommitteeSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(
        regionalCommitteeEditUrl(0 /*, expertCommissionSearch.countyId*/)
      );
    }
  };
  const handleDetailsClicked = (id?: number) => {
    if (id) {
      TableUtils.SetSessionStorageItem<RegionalCommitteeSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(regionalCommitteeEditUrl(id!));
    }
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  const handleCountyChange = (e: any) => {
    const county = Number.parseInt(e.target.value);
    if (!isNaN(county)) {
      const newExpertCommissionSearch: RegionalCommitteeSearch = {
        ...expertCommissionSearch,
        countyId: county,
      };
      setExpertCommissionSearch(newExpertCommissionSearch);
    }
  };
  const handleNameChange = (val: string) => {
    const newSearch: RegionalCommitteeSearch = {
      ...expertCommissionSearch,
      name: val,
    };
    setExpertCommissionSearch(newSearch);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={isEditable ? "show" : "disable"}
        showBack={"hide"}
        onClickAdd={handleNewExpertCommissionClicked}
        pageTitle={t("common.expertCommission.regionPageName")}
      />
      <SearchCard
        onClickSearch={() => {
          setTableSearch(expertCommissionSearch);
          setFilterChanged(false);
          search();
        }}
        onClickClearSearch={() => {
          setClearFilter(true);
          setExpertCommissionSearch(initialTableState);
          setTableSearch(initialTableState);
          search();
        }}
        isFilterChanged={filterChanged}
      >
        <Row>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.countyList")}
              name={nameof<typeof expertCommissionSearch>("countyId")}
              value={expertCommissionSearch.countyId}
              onChange={handleCountyChange}
              selectData={codeBookToSelectData(authCounties)}
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t("common.expertCommission.name")}
              name={nameof<typeof expertCommissionSearch>("name")}
              value={expertCommissionSearch.name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable tableProps={tableProps} />
    </ContentArea>
  );
}
